import { Link } from '@globalmed/atlas-ui';
import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './ContractPeriods.module.css';
import { ContractPeriodFields } from '../ContractPeriodFields';
import {
  EMPTY_CONTRACT,
  isContractPeriodRequired,
  TCreateOrganizationForm,
  TOrganizationContractForm,
} from '../../schema';
import { hasContractStarted } from '@/utils/organizationContracts';

const CONTRACT_FIELDS = [
  'seats',
  'stationLicenses',
  'consultsPerMonth',
  'contractStartDate',
] as const;

type TProps = {
  disabled?: boolean;
  occupiedSeats?: number;
  savedContracts?: TOrganizationContractForm[];
};

/**
 * The organization's contract periods, each with its own counts and dates,
 * and a link to add a new one. The periods scroll within a capped height,
 * keeping the add link in view. Every period but the first can be deleted
 * until it starts.
 * Renders nothing where the provisioning type and scope bill
 * on no contract.
 * @param props - Component props.
 * @param props.disabled - When true, every contract is locked for editing (Org
 * Admins) and no contract can be added or deleted.
 * @param props.occupiedSeats - The organization's current occupied seats,
 * forwarded to each contract's "cannot be lower than occupied" seats error.
 * @param props.savedContracts - The contracts as the organization was loaded,
 * used to lock the fields of a contract that has started and keep it from
 * being deleted.
 * @returns JSX.Element representing the contract periods, or `null`.
 */
export const ContractPeriods = ({
  disabled = false,
  occupiedSeats,
  savedContracts = [],
}: TProps) => {
  const {
    control,
    watch,
    subscribe,
    getValues,
    getFieldState,
    trigger,
    formState: { isSubmitting },
  } = useFormContext<TCreateOrganizationForm>();
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'provisioning.contracts',
    keyName: 'fieldKey',
  });

  const showContractPeriods = isContractPeriodRequired({
    type: watch('provisioning.type'),
    scope: watch('provisioning.scope'),
  });
  const hasSeveralContracts = fields.length > 1;
  useEffect(
    () =>
      subscribe({
        name: 'provisioning',
        formState: { values: true },
        callback: ({ name }) => {
          if (!name) return;
          const names = getValues('provisioning.contracts').flatMap(
            (contract, index) =>
              CONTRACT_FIELDS.filter(
                (field) =>
                  !!contract[field] ||
                  !!getFieldState(`provisioning.contracts.${index}.${field}`)
                    .error,
              ).map(
                (field) => `provisioning.contracts.${index}.${field}` as const,
              ),
          );
          if (names.length) void trigger(names);
        },
      }),
    [subscribe, getValues, getFieldState, trigger],
  );

  if (!showContractPeriods) return null;

  return (
    <div className={styles.contracts}>
      <div className={styles.list}>
        {fields.map((field, index) => {
          const savedContract = savedContracts.find(
            (contract) => !!field.id && contract.id === field.id,
          );
          const title = t('create-organization-modal.contract-period', {
            number: index + 1,
          });
          const isDeletable =
            !disabled &&
            index > 0 &&
            !hasContractStarted(savedContract?.contractStartDate);

          return (
            <section
              key={field.fieldKey}
              className={styles.contract}
              aria-label={title}
            >
              {hasSeveralContracts && (
                <div className={styles.header}>
                  <span className={styles.title}>{title}</span>
                  {isDeletable && (
                    <Link
                      preIcon="XClose"
                      color="destructive"
                      size="sm"
                      aria-label={t(
                        'create-organization-modal.delete-contract-period',
                        { number: index + 1 },
                      )}
                      disabled={isSubmitting}
                      onClick={() => remove(index)}
                    />
                  )}
                </div>
              )}
              <ContractPeriodFields
                index={index}
                disabled={disabled}
                occupiedSeats={occupiedSeats}
                savedContract={savedContract}
              />
            </section>
          );
        })}
      </div>

      {!disabled && (
        <Link
          className={styles.addLink}
          text={t('create-organization-modal.add-contract-period')}
          preIcon="Plus"
          disabled={isSubmitting}
          onClick={() => append(EMPTY_CONTRACT)}
        />
      )}
    </div>
  );
};
